import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, from } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import * as _ from 'lodash';

import { AccessState, Brand, BrandModel } from 'shared/models';

import { FcProductConnectionStatus, FcProductSuiteConnectionStatus } from '../brands/types';
import { getBrandFcProductsConnectionStatus } from '../brands/helpers';

import { BrandShopTertiaryNavigationService } from './brand-shop-tertiary-navigation/brand-shop-tertiary-navigation.service';
import { BrandShopNavigationService } from './brand-shop-navigation/brand-shop-navigation.service';
import { NavigationLink as BrandShopNavigationLink } from './brand-shop-navigation/types';
import { brandShopBaseUrl, fcProductNameToFcProductSuiteMapper } from './utils';
import { BrandsService } from '../brands/providers/brands.service';
import {
    NavigationAction as TertiaryNavigationAction,
    NavigationLink as TertiaryNavigationLink
} from './brand-shop-tertiary-navigation/types';
import { RetailerPrivilege } from 'helpers/team-user-roles';

@Injectable({
    providedIn: 'root'
})
export class BrandShopService {
    private _brandId = new BehaviorSubject<string>('');

    brand: Observable<Brand>;
    navigationLinks: Observable<BrandShopNavigationLink[]>;
    activePageTitle: Observable<string>;

    brandRequestedBeforeInit = new BehaviorSubject<Brand>(null);

    constructor(
        private brandShopTertiaryNavigationService: BrandShopTertiaryNavigationService,
        private brandShopNavigationService: BrandShopNavigationService,
        private brandsService: BrandsService,
        private brandModel: BrandModel
    ) {
        this.brand = this._brandId.pipe(
            filter((brandId) => !!brandId),
            distinctUntilChanged(),
            switchMap((brandId) => this.getBrand(brandId)),
            shareReplay(1)
        );

        this.navigationLinks = this.brand.pipe(
            tap((brand) => this.setNavigationLinks(brand._id, brand.fcProducts)),
            switchMap(() => this.brandShopNavigationService.navigationLinks),
            shareReplay(1)
        );

        this.activePageTitle = this.brandShopNavigationService.activePageTitle;
    }

    setBrandShopBrandId(brandId: Brand['_id']) {
        if (!brandId) return;
        this._brandId.next(brandId);
    }

    /**
     * Remove the selected tertiary navigation items.
     *
     * @param [links] {boolean} - Whether to remove the links. Defaults to TRUE.
     * @param [actions] {boolean} - Whether to remove the actions. Defaults to TRUE.
     */
    removeTertiaryNavigationItems(links = true, actions = true) {
        this.brandShopTertiaryNavigationService.removeItems(links, actions);
    }

    /**
     * Set the tertiary navigation items.
     *
     * @param [links] {@link TertiaryNavigationLink} - The links to display in the tertiary navigation.
     * @param [actions] {@link TertiaryNavigationAction} - The items that trigger actions in the tertiary navigation.
     *
     * @see {@link removeTertiaryNavigationItems} - Call it when destroying the component that invokes this method to avoid propagation of the tertiary navigation items to other components that do not require it.
     */
    setTertiaryNavigationItems({
        links,
        actions
    }: {
        links?: TertiaryNavigationLink[];
        actions?: TertiaryNavigationAction[];
    }) {
        this.brandShopTertiaryNavigationService.setItems(links, actions);
    }

    /**
     * Set the navigation links based on the FC products that the brand participates in.
     *
     * @param brandId
     * @param fcProducts
     */
    setNavigationLinks(brandId: Brand['_id'], fcProducts: Brand['fcProducts']) {
        this.brandShopNavigationService.setNavigationLinksByFcProducts(brandId, fcProducts);
        this.setReturnsNavigationLink(brandId);
    }

    /**
     * Append links to the brand shop navigation.
     *
     * @param links {BrandShopNavigationLink[]} - The links to append to the default ones.
     */
    appendNavigationLinks(links: BrandShopNavigationLink[]) {
        this.brandShopNavigationService.appendLinks(links);
    }

    getBrand(brandId: Brand['_id']): Observable<Brand> {
        if (this.brandRequestedBeforeInit.value?._id === brandId) {
            return this.brandRequestedBeforeInit;
        }

        return from(this.brandModel.getBrand(brandId)).pipe(
            tap((brand) => {
                this.brandRequestedBeforeInit.next(brand);
            })
        );
    }

    getBrandFcProductSuitesConnectionStatus(brandId: Brand['_id']): Observable<FcProductSuiteConnectionStatus[]> {
        return combineLatest([
            this.brandsService.fetchBrandsWithSuggestionsAndAutoRestockConnection(),
            this.getBrand(brandId)
        ]).pipe(
            map(([[brandsWithAutoRestockConnection, brandsWithSuggestionConnection], brand]) => {
                const brandFcProductsConnectionStatus = getBrandFcProductsConnectionStatus(
                    brand,
                    brandsWithAutoRestockConnection,
                    brandsWithSuggestionConnection
                );
                return brandFcProductsConnectionStatus.map(({ product, status }) => ({
                    suite: fcProductNameToFcProductSuiteMapper[product],
                    status:
                        status === AccessState.Granted
                            ? FcProductConnectionStatus.Connected
                            : status === AccessState.Pending
                              ? FcProductConnectionStatus.Pending
                              : FcProductConnectionStatus.Unconnected
                }));
            })
        );
    }

    private setReturnsNavigationLink(brandId: Brand['_id']): void {
        const brandsParticipatingInReturns = ['5d8b83f08ebb08001209e832'];

        if (!brandsParticipatingInReturns.includes(brandId)) {
            return;
        }

        this.appendNavigationLinks([
            {
                route: {
                    target: `/${brandShopBaseUrl(brandId)}/returns`
                },
                label: 'ORDER_RETURN_FORMS_OVERVIEW.TITLE',
                e2eHook: 'order-area-returns',
                privileges: { retailer: [RetailerPrivilege.Admin, RetailerPrivilege.Buyer] },
                hasChildren: false,
                index: 99 // Must be placed at the end position of the navigation links
            }
        ]);
    }
}
